import React from "react";

/**
 * Styles for the website onboarding (OnboardingModal + steps). Same palette,
 * type and motion as the web funnel: the shell renders FunnelScreenStyles next
 * to this and the steps use its `.fs-*` classes directly for the CTA, the name
 * field, the friend cards, the dividers and the character reveal. Everything
 * here is the shell itself and the pieces the funnel doesn't have (option
 * rows, chips, tags, the discovery grid, the adventure cards, the loader).
 *
 * Palette: ink #162642, subtitle #6B7892, accent #296FFB→#3E8DFD, soft card
 * fill #F2F6FD, field #F9FAFE, hairline #E4E9F5.
 */
export const OnboardingStyles = () => (
  <style>{`
    /* Shell — the onboarding is the only thing on the page, so it owns the
       viewport and does its own scrolling (never the body) */
    .ob-bg {
      position: fixed; inset: 0;
      z-index: 50;
      overflow-x: hidden; overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      background: linear-gradient(180deg, #243EA0 0%, #637BE9 50%, #BFABF1 100%);
      font-family: 'Inter', 'Fredoka', sans-serif;
      display: flex;
      padding: calc(22px + env(safe-area-inset-top, 0px))
               calc(16px + env(safe-area-inset-right, 0px))
               calc(24px + env(safe-area-inset-bottom, 0px))
               calc(16px + env(safe-area-inset-left, 0px));
      box-sizing: border-box;
    }
    .ob-bg * { box-sizing: border-box; }
    .ob-shell {
      width: 100%; max-width: 520px;
      margin: 0 auto;
      align-self: stretch;
      display: flex; flex-direction: column;
      position: relative; z-index: 1;
    }
    /* The shell is a column as tall as the screen, so the card would shrink
       to fit it and (being overflow: hidden) clip its own bottom; nothing in
       it shrinks — a tall card pushes the wordmark down and the shell scrolls */
    .ob-shell > * { flex-shrink: 0; }
    .ob-shell > .ob-card { margin: auto 0; }

    /* Step header: back button + one segment per step */
    .ob-header {
      display: flex; align-items: center; gap: 14px;
      margin: 0 4px 20px;
    }
    .ob-back-slot {
      width: 44px; height: 44px; flex-shrink: 0;
      transition: width 0.35s cubic-bezier(.22,1,.36,1);
    }
    .ob-back-slot:empty { width: 0; }
    .ob-back {
      width: 44px; height: 44px;
      display: flex; align-items: center; justify-content: center;
      background: #FFF;
      border: none; border-radius: 14px;
      color: #162642;
      cursor: pointer;
      box-shadow: 0 4px 14px rgba(40,62,128,0.10);
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: box-shadow 0.2s, color 0.2s;
    }
    .ob-back:hover { color: #296FFB; box-shadow: 0 6px 18px rgba(41,111,251,0.18); }
    .ob-segments { display: flex; gap: 7px; flex: 1; }
    .ob-segment {
      height: 6px; flex: 1; border-radius: 3px;
      background: rgba(255,255,255,0.3);
      overflow: hidden;
    }
    .ob-segment::after {
      content: '';
      display: block;
      height: 100%; width: 0;
      border-radius: 3px;
      background: #FFF;
      transition: width 0.6s cubic-bezier(.22,1,.36,1);
    }
    .ob-segment.is-filled::after { width: 100%; }
    .ob-step-count {
      flex-shrink: 0;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 13px; font-weight: 700;
      letter-spacing: 0.08em;
      color: rgba(255,255,255,0.88);
      font-variant-numeric: tabular-nums;
      text-shadow: 0 1px 8px rgba(36,48,140,0.35);
    }

    /* Card */
    .ob-card {
      position: relative;
      background: #FFF;
      border-radius: 32px;
      box-shadow: 0 24px 60px rgba(14,24,84,0.26), 0 2px 8px rgba(14,24,84,0.10);
      padding: 30px 24px 26px;
      text-align: center;
      overflow: hidden;
    }
    .ob-card .fs-sub { margin-bottom: 18px; }
    .ob-wordmark {
      text-align: center;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 15px; font-weight: 700;
      letter-spacing: 5px;
      color: rgba(255,255,255,0.88);
      text-shadow: 0 1px 10px rgba(36,48,140,0.35);
      margin: 20px 0 0;
      /* Air under the wordmark when a tall step (first story) scrolls to it */
      padding-bottom: 32px;
    }
    .ob-cta-row { margin-top: 18px; }
    .ob-link {
      display: inline-block;
      min-height: 44px;
      margin-top: 10px;
      padding: 12px 12px;
      background: none; border: none;
      font-family: 'Inter', sans-serif;
      font-size: 15px; font-weight: 600;
      color: #6B7892;
      text-decoration: underline; text-underline-offset: 3px;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      transition: color 0.2s;
    }
    .ob-link:hover { color: #296FFB; }
    .ob-secondary {
      flex: 1;
      min-height: 58px;
      padding: 14px 18px;
      border: 2px solid transparent; border-radius: 20px;
      background: #F2F6FD;
      color: #162642;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 18px; font-weight: 700;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: transform 180ms cubic-bezier(.2,.8,.2,1), background 0.2s;
    }
    .ob-secondary:hover { background: #E9EFFB; transform: translateY(-2px); }
    .ob-secondary:active { transform: scale(0.98); }
    .ob-actions { display: flex; gap: 10px; }
    .ob-actions .fs-cta { flex: 1.4; width: auto; }

    /* Mascot at the top of a step: a transparent clip of the panda with the
       funnel's star doodles around it */
    .ob-mascot {
      position: relative;
      width: 156px; height: 130px;
      margin: 0 auto 14px;
    }
    .ob-mascot.is-small { width: 124px; height: 104px; margin-bottom: 10px; }
    .ob-mascot.is-large { width: 200px; height: 166px; margin-bottom: 8px; }
    .ob-mascot-clip {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: contain;
      filter: drop-shadow(0 12px 18px rgba(22,38,66,0.12));
      /* The alpha clips keep a whisper of their matte; multiplied onto the
         white card it vanishes, and the panda's own colours are unchanged */
      mix-blend-mode: multiply;
    }
    .ob-mascot.is-still .ob-mascot-clip { animation: obFloat 2.8s ease-in-out infinite; }
    .ob-mascot-star-l { width: 34px; height: 34px; left: -2px; top: 36px; transform: rotate(-12deg); }
    .ob-mascot-star-r { width: 34px; height: 34px; right: -4px; top: 74px; transform: rotate(10deg); }
    .ob-mascot-lines { width: 40px; height: 40px; right: 12px; top: -4px; }
    .ob-mascot.is-small .ob-mascot-star-l { width: 28px; height: 28px; top: 28px; }
    .ob-mascot.is-small .ob-mascot-star-r { width: 28px; height: 28px; top: 58px; }
    .ob-mascot.is-small .ob-mascot-lines { width: 34px; height: 34px; right: 6px; }
    /* A zoomed clip grows upward out of its box, so the box moves down to
       make room, and the doodles step outward */
    .ob-mascot.is-zoomed { margin-top: calc((var(--ob-zoom) - 1) * 120px); }
    .ob-mascot.is-zoomed .ob-mascot-clip { transform: scale(var(--ob-zoom)); transform-origin: 50% 90%; }
    .ob-mascot.is-zoomed .ob-mascot-star-l { left: -30px; top: 16px; }
    .ob-mascot.is-zoomed .ob-mascot-star-r { right: -34px; top: 62px; }
    .ob-mascot.is-zoomed .ob-mascot-lines { right: -14px; top: -26px; }
    @keyframes obFloat {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-7px); }
    }

    /* Option rows (one choice) */
    .ob-options { display: grid; gap: 10px; margin-top: 2px; }
    .ob-option {
      position: relative;
      width: 100%;
      display: flex; align-items: center; gap: 14px;
      padding: 12px 14px 12px 12px;
      background: #F2F6FD;
      border: 2px solid transparent;
      border-radius: 20px;
      text-align: left;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), border-color 0.2s, background 0.2s, box-shadow 0.2s;
    }
    .ob-option:hover { transform: translateY(-2px); }
    .ob-option:active { transform: scale(0.98); }
    .ob-option.is-selected {
      background: #FFF;
      border-color: #296FFB;
      box-shadow: 0 14px 30px rgba(41,111,251,0.18), 0 0 0 5px rgba(41,111,251,0.10);
    }
    .ob-option-icon {
      width: 46px; height: 46px; flex-shrink: 0;
      border-radius: 15px;
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.3s cubic-bezier(.34,1.5,.5,1);
    }
    .ob-option.is-selected .ob-option-icon { transform: scale(1.1) rotate(-5deg); }
    .ob-option-title {
      flex: 1; min-width: 0;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 17px; font-weight: 600;
      color: #162642; line-height: 1.25;
    }
    .ob-option-sub {
      display: block;
      font-family: 'Inter', sans-serif;
      font-size: 13.5px; font-weight: 400;
      color: #6B7892;
      margin-top: 2px;
    }
    .ob-option-radio {
      width: 24px; height: 24px; flex-shrink: 0;
      border-radius: 50%;
      border: 2px solid #D3DCEC;
      background: #FFF;
      color: #FFF;
      display: flex; align-items: center; justify-content: center;
      transition: border-color 0.2s, background 0.2s;
    }
    .ob-option-radio svg {
      opacity: 0; transform: scale(0.4);
      transition: transform 0.3s cubic-bezier(.34,1.5,.5,1), opacity 0.15s;
    }
    .ob-option.is-selected .ob-option-radio {
      border-color: #296FFB;
      background: linear-gradient(135deg, #296FFB, #3E8DFD);
    }
    .ob-option.is-selected .ob-option-radio svg { opacity: 1; transform: scale(1); }

    /* A labelled group inside a card */
    .ob-section { text-align: left; }
    .ob-section-title {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 17px; font-weight: 600;
      color: #162642;
      margin: 0 0 10px 2px;
    }
    .ob-card .fs-field-wrap { margin-bottom: 20px; }
    .ob-card .fs-field-input { font-size: 20px; }
    .ob-textarea {
      display: block; width: 100%;
      padding: 0; border: none; outline: none;
      background: transparent;
      font-family: 'Inter', sans-serif;
      font-size: 18px; font-weight: 500; line-height: 1.4;
      color: #162642;
      resize: none;
    }
    .ob-textarea::placeholder { color: #B3BDCF; font-weight: 400; }

    /* Chips (age): fixed widths, so the seven ages wrap into two centred
       rows (4 + 3) on a phone and sit in one row on a desktop */
    .ob-chips { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
    .ob-chip {
      width: 64px; height: 48px;
      padding: 0;
      border-radius: 16px;
      background: #F2F6FD;
      border: 2px solid transparent;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 18px; font-weight: 700;
      color: #162642;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), background 0.2s, color 0.2s, box-shadow 0.2s;
    }
    .ob-chip:hover { transform: translateY(-2px); }
    .ob-chip:active { transform: scale(0.95); }
    .ob-chip.is-selected {
      background: linear-gradient(90deg, #296FFB 0%, #3E8DFD 100%);
      color: #FFF;
      transform: translateY(-2px) scale(1.04);
      box-shadow: 0 10px 22px rgba(41,111,251,0.30);
    }

    /* Tags (interests) */
    .ob-tags { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
    .ob-tag {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 6px 14px 6px 6px;
      border-radius: 999px;
      background: #F2F6FD;
      border: 2px solid transparent;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 15px; font-weight: 600;
      color: #162642;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), border-color 0.2s, background 0.2s, box-shadow 0.2s, color 0.2s, opacity 0.2s;
    }
    .ob-tag:hover { transform: translateY(-2px); }
    .ob-tag:active { transform: scale(0.95); }
    .ob-tag-emoji {
      width: 30px; height: 30px;
      border-radius: 50%;
      background: #FFF;
      display: flex; align-items: center; justify-content: center;
      font-size: 17px; line-height: 1;
      box-shadow: 0 2px 6px rgba(22,38,66,0.06);
      transition: transform 0.3s cubic-bezier(.34,1.5,.5,1);
    }
    .ob-tag.is-selected {
      background: #FFF;
      border-color: #296FFB;
      color: #296FFB;
      transform: translateY(-2px);
      box-shadow: 0 10px 22px rgba(41,111,251,0.16), 0 0 0 4px rgba(41,111,251,0.10);
    }
    .ob-tag.is-selected .ob-tag-emoji { transform: scale(1.12) rotate(-8deg); }
    .ob-tag.is-dim { opacity: 0.45; }
    .ob-tag.is-more {
      background: #FFF;
      border: 2px dashed #C9D6EE;
      color: #2277FF;
      padding: 6px 14px;
    }
    .ob-tag.is-more:hover { border-color: #296FFB; }
    .ob-count {
      margin: 12px 0 0;
      font-family: 'Inter', sans-serif;
      font-size: 13.5px; color: #6B7892;
    }
    .ob-count b { color: #296FFB; font-weight: 700; }
    .ob-count-hint { color: #8A96AD; }
    .ob-count.is-full .ob-count-hint { color: #296FFB; font-weight: 600; }
    .ob-tag.is-shake { animation: obShake 0.35s ease; }
    @keyframes obShake {
      0%, 100% { transform: translateX(0); }
      20% { transform: translateX(-5px) rotate(-2deg); }
      50% { transform: translateX(5px) rotate(2deg); }
      80% { transform: translateX(-3px); }
    }

    /* Search / add-your-own field */
    .ob-search {
      display: flex; align-items: center; gap: 10px;
      height: 54px;
      padding: 0 6px 0 14px;
      margin-bottom: 14px;
      background: #F9FAFE;
      border: 1.5px solid #E4E9F5;
      border-radius: 18px;
      transition: border-color 0.2s, box-shadow 0.25s, background 0.2s;
    }
    .ob-search:focus-within {
      border-color: #296FFB;
      background: #FFF;
      box-shadow: 0 0 0 4px rgba(41,111,251,0.12);
    }
    .ob-search svg { flex-shrink: 0; color: #8A96AD; }
    .ob-search input {
      flex: 1; min-width: 0;
      border: none; outline: none; background: transparent;
      font-family: 'Inter', sans-serif;
      font-size: 16px; font-weight: 500;
      color: #162642;
    }
    .ob-search input::placeholder { color: #B3BDCF; font-weight: 400; }
    .ob-search-add {
      height: 40px; flex-shrink: 0;
      padding: 0 14px;
      border: none; border-radius: 13px;
      background: linear-gradient(90deg, #296FFB, #3E8DFD);
      color: #FFF;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 14px; font-weight: 700;
      cursor: pointer;
      box-shadow: 0 6px 14px rgba(41,111,251,0.25);
      animation: obPop 0.3s cubic-bezier(.34,1.5,.5,1);
    }
    .ob-search-add:disabled { opacity: 0.6; cursor: default; }

    /* Discovery sources */
    .ob-sources { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
    .ob-source {
      position: relative;
      display: flex; flex-direction: column; align-items: center; gap: 8px;
      padding: 14px 4px 11px;
      background: #F2F6FD;
      border: 2px solid transparent;
      border-radius: 18px;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), border-color 0.2s, background 0.2s, box-shadow 0.2s;
    }
    .ob-source:hover { transform: translateY(-2px); }
    .ob-source:active { transform: scale(0.96); }
    .ob-source.is-selected {
      background: #FFF;
      border-color: #296FFB;
      transform: translateY(-3px);
      box-shadow: 0 12px 24px rgba(41,111,251,0.16), 0 0 0 4px rgba(41,111,251,0.10);
    }
    .ob-source-icon {
      width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center;
      font-size: 26px; line-height: 1;
    }
    .ob-source-icon img { width: 30px; height: 30px; object-fit: contain; }
    .ob-source-name {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 12.5px; font-weight: 600;
      color: #162642; line-height: 1.1;
      transition: color 0.2s;
    }
    .ob-source.is-selected .ob-source-name { color: #296FFB; }
    .ob-check-badge {
      position: absolute; top: -7px; right: -7px;
      width: 24px; height: 24px;
      border-radius: 50%;
      background: linear-gradient(135deg, #296FFB, #3E8DFD);
      color: #FFF;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 4px 10px rgba(41,111,251,0.35);
      animation: obPop 0.4s cubic-bezier(.34,1.5,.5,1);
    }
    @keyframes obPop { from { transform: scale(0); } to { transform: scale(1); } }

    /* Avatar step: the chosen photo, and the camera */
    .ob-card .fs-friends { margin-top: 0; }
    .ob-card .fs-divider { margin: 20px 0 14px; }
    .ob-portrait {
      position: relative;
      width: 200px; height: 200px;
      margin: 4px auto 18px;
    }
    .ob-portrait::before {
      content: '';
      position: absolute; inset: -14px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(62,141,253,0.38), rgba(62,141,253,0) 70%);
      filter: blur(10px);
      animation: obPulse 2.4s ease-in-out infinite;
    }
    .ob-portrait img {
      position: relative;
      width: 100%; height: 100%;
      object-fit: cover;
      border-radius: 50%;
      border: 6px solid #FFF;
      background: #F2F6FD;
      box-shadow: 0 16px 36px rgba(41,111,251,0.22), 0 0 0 3px rgba(41,111,251,0.18);
      animation: obPop 0.6s cubic-bezier(.34,1.45,.5,1);
    }
    .ob-camera {
      position: relative;
      width: 100%;
      aspect-ratio: 4 / 3;
      margin-bottom: 14px;
      border-radius: 24px;
      overflow: hidden;
      background: #0E1A3C;
      box-shadow: 0 16px 36px rgba(14,24,84,0.25);
    }
    .ob-camera video {
      width: 100%; height: 100%;
      object-fit: cover;
      transform: scaleX(-1);
      display: block;
    }

    /* Meet — confetti falls past the card's edge */
    .ob-confetti {
      position: absolute; inset: 0;
      pointer-events: none;
      overflow: hidden;
      border-radius: 32px;
      z-index: 1;
    }
    .ob-confetti span {
      position: absolute;
      top: -20px;
      width: 11px; height: 11px;
      border-radius: 2px;
      animation-name: obConfettiFall;
      animation-timing-function: ease-in;
      animation-fill-mode: forwards;
    }
    @keyframes obConfettiFall {
      to {
        transform: translate(var(--drift), 640px) rotate(var(--spin));
        opacity: 0;
      }
    }
    .ob-card .fs-hero { margin-bottom: 20px; }

    /* Loader (avatar being drawn, story being made) */
    .ob-loader { padding: 4px 0 2px; }
    .ob-loader .fs-title { margin-bottom: 10px; }
    .ob-msg {
      min-height: 48px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 16px;
    }
    .ob-msg .fs-sub { margin: 0; }
    .ob-progress {
      position: relative;
      height: 12px;
      border-radius: 6px;
      background: #DDE6F2;
      overflow: hidden;
    }
    .ob-progress-fill {
      position: absolute; top: 0; bottom: 0; left: -40%;
      width: 40%;
      border-radius: 6px;
      background: linear-gradient(90deg, #296FFB, #3E8DFD);
      animation: obIndeterminate 1.5s cubic-bezier(.4,0,.2,1) infinite;
    }
    @keyframes obIndeterminate { to { left: 100%; } }
    @keyframes obPulse {
      0%, 100% { transform: scale(1); opacity: 0.9; }
      50% { transform: scale(1.06); opacity: 1; }
    }

    /* First adventure */
    .ob-ideas { display: flex; flex-wrap: wrap; gap: 8px; margin: -6px 0 2px; }
    .ob-idea {
      min-height: 40px;
      padding: 8px 14px;
      border-radius: 999px;
      background: #EEF3FF;
      border: 1.5px solid transparent;
      color: #296FFB;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 13.5px; font-weight: 600;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), background 0.2s, color 0.2s;
    }
    .ob-idea:hover { transform: translateY(-2px); }
    .ob-idea:active { transform: scale(0.95); }
    .ob-idea.is-selected {
      background: linear-gradient(90deg, #296FFB, #3E8DFD);
      color: #FFF;
      box-shadow: 0 8px 18px rgba(41,111,251,0.25);
    }
    .ob-adventures { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .ob-adventure {
      position: relative;
      aspect-ratio: 4 / 3;
      padding: 0;
      border: 3px solid transparent;
      border-radius: 20px;
      overflow: hidden;
      background: #E3F0FF;
      text-align: left;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      box-shadow: 0 6px 16px rgba(22,38,66,0.10);
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), box-shadow 0.2s, border-color 0.2s;
    }
    .ob-adventure img {
      width: 100%; height: 100%;
      object-fit: cover; display: block;
      transition: transform 0.4s ease;
    }
    .ob-adventure:hover img { transform: scale(1.05); }
    .ob-adventure:active { transform: scale(0.97); }
    .ob-adventure.is-selected {
      border-color: #296FFB;
      transform: translateY(-3px);
      box-shadow: 0 16px 32px rgba(41,111,251,0.26), 0 0 0 4px rgba(41,111,251,0.12);
    }
    .ob-adventure-scrim {
      position: absolute; left: 0; right: 0; bottom: 0;
      padding: 26px 12px 10px;
      background: linear-gradient(180deg, rgba(11,26,51,0) 0%, rgba(11,26,51,0.74) 80%);
      color: #FFF;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 15px; font-weight: 700;
      text-shadow: 0 1px 4px rgba(0,0,0,0.35);
    }
    .ob-adventure-fallback {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      font-size: 52px;
    }
    .ob-adventure .ob-check-badge { top: 8px; right: 8px; }

    /* Phones */
    @media (max-width: 480px) {
      .ob-bg {
        padding: calc(14px + env(safe-area-inset-top, 0px))
                 calc(12px + env(safe-area-inset-right, 0px))
                 calc(18px + env(safe-area-inset-bottom, 0px))
                 calc(12px + env(safe-area-inset-left, 0px));
      }
      .ob-header { gap: 12px; margin-bottom: 16px; }
      .ob-card { padding: 24px 16px 22px; border-radius: 28px; }
      .ob-confetti { border-radius: 28px; }
      .ob-wordmark { font-size: 13px; margin-top: 16px; padding-bottom: 28px; }
      .ob-chip { width: 62px; height: 46px; font-size: 17px; }
      .ob-sources { gap: 8px; }
      .ob-source { padding: 12px 2px 10px; border-radius: 16px; }
      .ob-source-name { font-size: 12px; }
      .ob-portrait { width: 176px; height: 176px; }
      .ob-option { gap: 12px; padding: 11px 12px 11px 11px; }
      .ob-option-title { font-size: 16px; }
    }
    @media (max-width: 374px) {
      .ob-card { padding: 20px 12px 18px; }
      .ob-mascot { width: 132px; height: 110px; }
      .ob-chips { gap: 8px; }
      .ob-chip { width: 58px; }
      .ob-tag { font-size: 14px; }
      .ob-source-name { font-size: 11px; }
      .ob-segments { gap: 5px; }
    }
    /* Short viewports: the decorative pieces shrink so the button stays in view */
    @media (max-height: 640px) {
      .ob-bg { padding-top: 10px; padding-bottom: 12px; }
      .ob-header { margin-bottom: 12px; }
      .ob-mascot, .ob-mascot.is-small { width: 112px; height: 94px; margin-bottom: 6px; }
      .ob-mascot.is-large { width: 150px; height: 124px; }
      .ob-wordmark { display: none; }
    }

    @media (prefers-reduced-motion: reduce) {
      .ob-option:hover, .ob-chip:hover, .ob-tag:hover, .ob-source:hover,
      .ob-idea:hover, .ob-secondary:hover, .ob-adventure:hover { transform: none; }
      .ob-adventure:hover img { transform: none; }
      .ob-confetti { display: none; }
      .ob-check-badge, .ob-search-add, .ob-portrait img, .ob-tag.is-shake { animation: none; }
      .ob-portrait::before, .ob-mascot.is-still .ob-mascot-clip { animation: none; }
    }
  `}</style>
);

export default OnboardingStyles;
